import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CustomLink } from "core/components/CustomLink";
import { useFormatDate } from "core/hooks/useFormatDate";
import type {
  IBannerDto,
  ICategorySummaryDto,
} from "features/NewsEvents/entities";
import { getImageSRC } from "features/NewsEvents/utils";
import Image from "next/legacy/image";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";

export interface NewsCardProps {
  id: string;
  title: string;
  blurb: string;
  articleBanner: IBannerDto | undefined;
  articleType: ICategorySummaryDto | undefined;
  articleCategory: ICategorySummaryDto | undefined;
  publishedTime: Date | string;
  onChipClick: () => void;
  onCategoryClick: () => void;
  isImportant?: boolean;
  ref?: Ref<HTMLDivElement>;
}

export function NewsCard({
  id,
  title,
  blurb,
  publishedTime,
  articleBanner = { blurDataUrl: "", altText: "", blobName: "" },
  articleCategory,
  articleType,
  onChipClick,
  onCategoryClick,
  isImportant,
  ref,
}: NewsCardProps) {
  const { blurDataUrl, altText, blobName } = articleBanner;
  const formatDate = useFormatDate();
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:NewsCard",
  });
  const formattedDate = formatDate(publishedTime);
  const href = `/news/${id}`;

  return (
    <Card
      ref={ref}
      sx={{
        display: "flex",
        position: "relative",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {isImportant && (
        <PriorityHighRoundedIcon
          sx={{
            zIndex: 1,
            position: "absolute",
            top: ({ spacing }) => spacing(1),
            left: ({ spacing }) => spacing(1),
            color: "red",
          }}
        />
      )}
      {articleType && (
        <Chip
          onClick={onChipClick}
          color="primary"
          label={articleType.title}
          sx={{
            zIndex: 1,
            position: "absolute",
            top: ({ spacing }) => spacing(1),
            right: ({ spacing }) => spacing(1),
          }}
        />
      )}
      <CustomLink href={href} passHref>
        <Box height={250} position="relative" width="100%">
          {blurDataUrl && blobName ? (
            <Image
              src={getImageSRC(blobName)}
              layout="fill"
              objectFit="cover"
              objectPosition="center"
              placeholder="blur"
              alt={altText || t("missingAltText")}
              blurDataURL={blurDataUrl}
              sizes="(max-width: 675px) 100vw, (max-width: 1000px) 45vw, 33vw"
            />
          ) : (
            <Box
              sx={{ height: "100%", width: "100%", backgroundColor: "grey" }}
            />
          )}
        </Box>
      </CustomLink>

      <CardContent sx={{ flex: 1 }}>
        <Typography
          color="primary"
          sx={{
            "&:hover": { cursor: "pointer" },
            fontWeight: 400,
            fontSize: "0.75rem",
          }}
          onClick={onCategoryClick}
        >
          {articleCategory?.title ?? t("uncategorized")} {">"}
        </Typography>
        <CustomLink href={href} passHref>
          <Typography variant="h5">{title}</Typography>
        </CustomLink>
        <Typography gutterBottom variant="body2" color="text.secondary">
          {formattedDate}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            display: "-webkit-box",
            overflow: "hidden",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 3,
          }}
        >
          {blurb}
        </Typography>
      </CardContent>
      <Stack px={3} pb={2} direction="row" justifyContent="flex-end">
        <Button
          LinkComponent={CustomLink}
          href={href}
          size="small"
          endIcon={<KeyboardDoubleArrowRightIcon sx={{ mb: "2px" }} />}
        >
          {t("readMore")}
        </Button>
      </Stack>
    </Card>
  );
}
